import type { Tag } from '@core/domain/model';
import { guard, type DataResult } from '@core/domain/result';
import { newId, toTag } from './mappers';
import type { Database } from './sqlite';

/**
 * Теги дел. Тег заводит сам юрист под свою практику: «банкротство», «срочное», «VIP» —
 * единственная классификация, которую мы не придумываем за него ([D-19]).
 */
export function tagRepository(db: Database, now: () => number = Date.now) {
  return {
    async all(): Promise<Tag[]> {
      const rows = await db.query('SELECT * FROM tags ORDER BY name COLLATE NOCASE');
      return rows.map(toTag);
    },

    async ofCase(caseId: string): Promise<Tag[]> {
      const rows = await db.query(
        `SELECT t.* FROM tags t JOIN case_tags ct ON ct.tagId = t.id
          WHERE ct.caseId = ? ORDER BY t.name COLLATE NOCASE`,
        [caseId],
      );
      return rows.map(toTag);
    },

    /** Теги всех дел разом: список дел рисует метки, не делая запрос на строку. */
    async byCase(): Promise<Map<string, Tag[]>> {
      const rows = await db.query<{ caseId: string; id: string; name: string }>(
        `SELECT ct.caseId AS caseId, t.id AS id, t.name AS name
           FROM case_tags ct JOIN tags t ON t.id = ct.tagId
          ORDER BY t.name COLLATE NOCASE`,
      );

      const byCase = new Map<string, Tag[]>();
      for (const row of rows) {
        const list = byCase.get(row.caseId) ?? [];
        list.push({ id: row.id, name: row.name });
        byCase.set(row.caseId, list);
      }
      return byCase;
    },

    /**
     * Повесить тег на дело по имени: находится существующий или заводится новый.
     * Юристу не нужно знать, существовал он уже или нет; сравнение без учёта регистра.
     */
    async attach(caseId: string, name: string): Promise<DataResult<string>> {
      return guard(async () => {
        const trimmed = name.trim();

        return db.transaction(async (tx) => {
          /*
           * Имя ищется перебором, а не запросом.
           *
           * `COLLATE NOCASE` в SQLite приводит регистр только у латиницы: «Срочное»
           * и «срочное» остались бы двумя разными тегами. Ровно то же решение принято
           * на Android — там сравнение живёт в Kotlin (TagDao.allOnce). Тегов у юриста
           * десятки, перебор дешевле своей таблицы нормализованных имён.
           */
          const all = await tx.query<{ id: string; name: string }>('SELECT id, name FROM tags');
          const existing = all.find(
            (tag) => tag.name.localeCompare(trimmed, 'ru', { sensitivity: 'accent' }) === 0,
          );

          const id = existing?.id ?? newId();
          if (!existing) await tx.run('INSERT INTO tags (id, name) VALUES (?, ?)', [id, trimmed]);

          await tx.run('INSERT OR IGNORE INTO case_tags (caseId, tagId) VALUES (?, ?)', [
            caseId,
            id,
          ]);
          await tx.run('UPDATE cases SET updatedAt = ? WHERE id = ?', [now(), caseId]);
          return id;
        });
      });
    },

    async attachExisting(caseId: string, tagId: string): Promise<DataResult<void>> {
      return guard(async () => {
        await db.run('INSERT OR IGNORE INTO case_tags (caseId, tagId) VALUES (?, ?)', [
          caseId,
          tagId,
        ]);
        await db.run('UPDATE cases SET updatedAt = ? WHERE id = ?', [now(), caseId]);
      });
    },

    /** Снять тег с дела. Тег, не оставшийся ни на одном деле, исчезает. */
    async detach(caseId: string, tagId: string): Promise<DataResult<void>> {
      return guard(async () => {
        await db.transaction(async (tx) => {
          await tx.run('DELETE FROM case_tags WHERE caseId = ? AND tagId = ?', [caseId, tagId]);
          await tx.run(
            'DELETE FROM tags WHERE id = ? AND NOT EXISTS (SELECT 1 FROM case_tags WHERE tagId = ?)',
            [tagId, tagId],
          );
          await tx.run('UPDATE cases SET updatedAt = ? WHERE id = ?', [now(), caseId]);
        });
      });
    },
  };
}

export type TagRepository = ReturnType<typeof tagRepository>;
